import { inject, Injectable } from '@angular/core';
import { OccCustomerConsignment, ReceiptTypeCodes } from '@checkout-v2/const';
import {
  CheckoutActions,
  CheckoutSelectors,
  PLACED_ORDER_PROCESS_ID,
  StateWithCustomCheckout,
} from '@checkout-v2/data-access';
import { select, Store } from '@ngrx/store';
import { CustomOrder } from '@order/const';
import { ProcessSelectors, StateWithProcess } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private readonly store = inject(Store<StateWithCustomCheckout>);
  private readonly processStateStore = inject(Store<StateWithProcess<void>>);

  init(): void {
    this.store.dispatch(new CheckoutActions.CheckoutInit());
  }

  destroy(): void {
    this.store.dispatch(new CheckoutActions.CheckoutDestroy());
  }

  placeOrder(): void {
    this.store.dispatch(new CheckoutActions.PlaceOrder());
  }

  getCustomerConsignment(): Observable<OccCustomerConsignment> {
    return this.store.select(CheckoutSelectors.getCustomerConsignmentSuccess).pipe(
      filter((success) => Boolean(success)),
      switchMap(() => this.store.select(CheckoutSelectors.getCustomerConsignmentValue)),
    );
  }

  getCustomerConsignmentError(): Observable<boolean> {
    return this.store.select(CheckoutSelectors.getCustomerConsignmentError);
  }

  getOrderDetails(): Observable<CustomOrder> {
    return this.store.pipe(select(CheckoutSelectors.getOrderDetails));
  }

  setOrderDetails(orderDetails: CustomOrder): void {
    return this.store.dispatch(new CheckoutActions.SetOrderDetails(orderDetails));
  }

  getReceiptType(): Observable<ReceiptTypeCodes | undefined> {
    return this.store.pipe(select(CheckoutSelectors.getActiveReceiptType));
  }

  updateReceiptType(type: ReceiptTypeCodes): void {
    this.getReceiptType()
      .pipe(
        take(1),
        filter((currnetType) => currnetType !== type),
      )
      .subscribe(() => {
        this.store.dispatch(new CheckoutActions.SetActiveReceiptType(type));
      });
  }

  getPlaceOrderError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(PLACED_ORDER_PROCESS_ID)));
  }

  getPlaceOrderLoading(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessLoadingFactory(PLACED_ORDER_PROCESS_ID)));
  }

  clearPlaceOrderState(): void {
    this.store.dispatch(new CheckoutActions.ClearPlaceOrder());
  }
}
